import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, MessageSquare, Pencil } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRAIN } from "@/constants/testIds";

export default function TrainerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [err, setErr] = useState("");
  const [answers, setAnswers] = useState({});
  const [savingQ, setSavingQ] = useState({});

  const reload = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/training/trainer/learners"),
        api.get("/training/trainer/questions"),
      ]);
      setLearners(a.data); setQuestions(b.data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const sendAnswer = async (qid) => {
    const txt = (answers[qid] || "").trim();
    if (!txt) return;
    setSavingQ((s) => ({ ...s, [qid]: true }));
    try {
      await api.post(`/training/trainer/questions/${qid}/answer`, { answer: txt });
      setAnswers((a) => ({ ...a, [qid]: "" }));
      await reload();
    } finally { setSavingQ((s) => ({ ...s, [qid]: false })); }
  };

  if (err) return <div className="glass-panel p-6 text-rose-300">{err}</div>;

  return (
    <div data-testid={TRAIN.trainerHub} className="space-y-10">
      <header className="fade-up">
        <div className="eyebrow">Trainer console</div>
        <h1 className="headline-xl">{user?.name?.split(" ")[0]}'s trainees</h1>
        <p className="text-lifted-dim mt-2 max-w-2xl">View assigned learners' progress, answer their questions and leave coaching notes.</p>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass-panel p-5 lg:col-span-2">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Users className="h-3 w-3" /> Assigned learners</div>
          {learners.length === 0
            ? <div className="mt-3 text-sm text-lifted-mute">No learners assigned yet.</div>
            : (
              <ul className="mt-3 divide-y divide-white/5">
                {learners.map((row) => (
                  <li key={row.id} data-testid={TRAIN.trainerLearnerRow(row.learner_id)} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-bebas tracking-[0.14em] text-lifted-text">{row.learner?.name}</div>
                      <div className="text-xs text-lifted-dim">{row.learner?.email} · Track {row.track_key}</div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      {(row.progress_by_track || []).map((p) => (
                        <span key={p._id} className="chip">{p._id}: {p.completed}/{p.total}</span>
                      ))}
                      <button
                        data-testid={TRAIN.trainerLearnerOpen(row.learner_id)}
                        onClick={() => navigate(`/trainer/learner/${row.learner_id}`)}
                        className="btn-ghost !py-1.5 !px-3 !text-[0.7rem]"
                      ><Pencil className="h-3 w-3" /> Open</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </div>

        <div className="glass-panel p-5">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Open questions</div>
          {questions.length === 0
            ? <div className="mt-3 text-sm text-lifted-mute">No open questions.</div>
            : (
              <ul className="mt-3 space-y-3">
                {questions.map((q) => (
                  <li key={q.id} data-testid={TRAIN.trainerQuestionRow(q.id)} className="border-l-2 border-lifted-purple-soft/40 pl-3 py-1">
                    <div className="text-sm text-lifted-text">{q.body}</div>
                    <div className="text-xs text-lifted-mute mt-0.5">{q.user_name} · {q.track_key}</div>
                    <div className="mt-2 flex gap-2">
                      <input
                        data-testid={TRAIN.trainerAnswerInput(q.id)}
                        className="input-lifted !py-1.5 !px-3 text-xs flex-1"
                        value={answers[q.id] || ""}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                        placeholder="Type answer…"
                      />
                      <button
                        data-testid={TRAIN.trainerAnswerSubmit(q.id)}
                        onClick={() => sendAnswer(q.id)}
                        disabled={savingQ[q.id]}
                        className="btn-lifted !py-1.5 !px-3 !text-[0.65rem]"
                      >Send</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </section>

      <Link to="/training" className="btn-ghost inline-flex">← Back to training</Link>
    </div>
  );
}
